import React, { useMemo } from "react";
import { find, isEqual, last, map } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { Facet } from "@linkfluence/models/facet";
import { ageFromDate } from "@tootools/date";
import Slider, { Mark } from "@ui-kit/form/slider";
import Icon from "@ui-kit/icon";
import Panel from "@ui-kit/popover/panel";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isDateRangeClause, isRangeClause } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Root = styled.div`
  min-width: 400px;
  padding: 12px 12px 24px;
`;

const Label = styled.div<{ isAgeRange?: boolean }>`
  display: ${({ isAgeRange }) => (isAgeRange ? "initial" : "flex")};
  align-items: center;
  margin-left: ${({ isAgeRange }) => isAgeRange && "8px"};
`;

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const StyledIcon = styled(Icon)`
  margin-top: -2px;
  margin-left: 4px;
`;

const isReviewRange = (facet: string) => facet === Facet.ReviewRate;

const isAgeRange = (facet: string) => facet === Facet.AuthorBirthday;

function formatRange(facet: FullFacet, range: [number, number]) {
  return isReviewRange(facet.id)
    ? { gt: range[0], lt: range[1] }
    : isAgeRange(facet.id)
    ? {
        gt: range[1] === last(facet.values).max ? null : `now-${range[1]}y`,
        lt: range[0] === facet.values[0].min ? null : `now-${range[0]}y`,
      }
    : { gt: range[0], lt: range[1] };
}

function getRangefromClause(clause: Clause, facet: string): [number, number] {
  if (isReviewRange(facet)) {
    const rangeClause = find(clause.and, isRangeClause);
    if (rangeClause) {
      return [rangeClause[facet].gt, rangeClause[facet].lt];
    } else {
      return [0, 0];
    }
  } else if (isAgeRange(facet)) {
    const dateRangeClause = find(clause.and, isDateRangeClause);
    if (
      dateRangeClause &&
      Object.values(dateRangeClause[facet]).filter((v) => v).length > 0
    ) {
      const lowerBound = ageFromDate(dateRangeClause[facet].lt);
      const upperBound = !dateRangeClause[facet].gt
        ? 110
        : ageFromDate(dateRangeClause[facet].gt);
      return [lowerBound, upperBound];
    } else {
      return [0, 110];
    }
  } else {
    return [0, 100];
  }
}

function getMarks(facet: FullFacet) {
  if (isReviewRange(facet.id)) {
    return map(facet.values, ({ id, min, max }) => ({
      id: "" + id,
      glyph: "star-filled",
      min: min,
      max: max,
    }));
  } else {
    return map(facet.values, ({ id, min, max }) => ({
      id: "" + id,
      min,
      max,
    }));
  }
}

const RadarlyFilterbarPanelsSlider = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const range = getRangefromClause(clause, facet.id);
  const clearClause = (clause, facet) =>
    refineClauseWithInnerClause(clause, { [facet.id]: {} }, true);

  const onRangeChange = (range: [number, number]) => {
    if (
      facet.id === Facet.AuthorBirthday &&
      isEqual(range, [facet.values[0].min, last(facet.values).max])
    ) {
      return setClause(clearClause(clause, facet));
    }
    if (
      facet.id === Facet.ReviewRate &&
      isEqual(range, [facet.values[0].min, facet.values[0].min])
    ) {
      return setClause(clearClause(clause, facet));
    }
    return setClause(
      refineClauseWithInnerClause(clause, {
        [facet.id]: formatRange(facet, range),
      })
    );
  };
  const marks = useMemo(() => {
    return getMarks(facet);
  }, [facet]);

  const onClear = () => setClause(clearClause(clause, facet));

  const isDisabled = isEqual(range, [
    marks[0].min,
    marks[marks.length - 1].max,
  ]);

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isDisabled : false}
      validateLabel={validateLabel}
      className={className}
    >
      <Root>
        {facet.description && <Description>{facet.description}</Description>}
        <Slider
          range={range}
          onChange={onRangeChange}
          marks={marks}
          renderMark={(mark: Mark) => (
            <Label isAgeRange={isAgeRange(facet.id)}>
              {mark.id}
              <StyledIcon glyph={mark.glyph} />
            </Label>
          )}
        />
      </Root>
    </Panel>
  );
};

export default RadarlyFilterbarPanelsSlider;
